import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CachedUrl {
  url: string;
  expiresAt: number;
}

const urlCache = new Map<string, CachedUrl>();

export const useSignedStorageUrl = (bucket: string, path: string | null) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }

    const cacheKey = `${bucket}/${path}`;
    const cached = urlCache.get(cacheKey);
    const now = Date.now();

    // Check cache: if URL exists and expires in more than 10 minutes, use it
    if (cached && cached.expiresAt > now + 10 * 60 * 1000) {
      setUrl(cached.url);
      return;
    }

    const getSignedUrl = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrl(path, 3600); // 1 hour expiry

        if (!isMountedRef.current) return;

        if (error) {
          console.error('Error creating signed URL:', error);
          setUrl(null);
        } else {
          const newUrl = data.signedUrl;
          const expiresAt = now + 3600 * 1000; // 1 hour from now
          
          // Cache the URL
          urlCache.set(cacheKey, { url: newUrl, expiresAt });
          setUrl(newUrl);
        }
      } catch (error) {
        if (!isMountedRef.current) return;
        console.error('Error creating signed URL:', error);
        setUrl(null);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    getSignedUrl();
  }, [bucket, path]);

  return { url, loading };
};

/**
 * Batch-variant: tekent meerdere paden in een ronde.
 *
 * Nodig voor galerijen. `useSignedStorageUrl` per afbeelding aanroepen kan niet
 * (dat zou een hook in een loop zijn) en zou bovendien N requests doen waar de
 * storage-API er een aankan.
 *
 * Deelt de cache met de enkelvoudige variant, dus een pad dat hier getekend is
 * hoeft elders niet opnieuw.
 */
export const useSignedStorageUrls = (bucket: string, paths: string[]) => {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  // De array-identiteit wisselt elke render; de inhoud is wat telt.
  const pathKey = paths.join(' ');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const wanted = pathKey ? pathKey.split(' ') : [];
    if (wanted.length === 0) {
      setUrls({});
      return;
    }

    const now = Date.now();
    const resolved: Record<string, string> = {};
    const missing: string[] = [];

    for (const path of wanted) {
      const cached = urlCache.get(`${bucket}/${path}`);
      if (cached && cached.expiresAt > now + 10 * 60 * 1000) {
        resolved[path] = cached.url;
      } else {
        missing.push(path);
      }
    }

    setUrls(resolved);
    if (missing.length === 0) return;

    const sign = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrls(missing, 3600);

        if (!isMountedRef.current) return;

        if (error) {
          console.error('Error creating signed URLs:', error);
          return;
        }

        const expiresAt = Date.now() + 3600 * 1000;
        const next = { ...resolved };
        for (const entry of data ?? []) {
          // De API geeft per pad een eigen error terug; een ontbrekend bestand
          // mag de rest van de galerij niet meeslepen.
          if (entry.error || !entry.signedUrl || !entry.path) continue;
          urlCache.set(`${bucket}/${entry.path}`, { url: entry.signedUrl, expiresAt });
          next[entry.path] = entry.signedUrl;
        }
        setUrls(next);
      } catch (error) {
        if (!isMountedRef.current) return;
        console.error('Error creating signed URLs:', error);
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };

    sign();
  }, [bucket, pathKey]);

  return { urls, loading };
};
